import React, { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import Checkbox from "@/components/ui/CheckBox";
import { FaArrowLeft } from "react-icons/fa";
import useRoleStore, { PermissionSection } from "./store/rolesStore";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import useGlobalStore from "@/store/globalStore";
import Button from "@/components/ui/Button";
import Spinner from "@/components/ui/Spinner";

const UserPermissions: React.FC = () => {
  const [searchParams] = useSearchParams();
  const roleId = searchParams.get("id");
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    getPermissions,
    setRoleById,
    getRoles,
    permissions,
    selectedRole,
    savePermissions,
    setPermissions,
  } = useRoleStore(
    useShallow((state) => ({
      getPermissions: state.getPermissions,
      setRoleById: state.setRoleById,
      getRoles: state.getRoles,
      permissions: state.permissions,
      selectedRole: state.selectedRole,
      savePermissions: state.savePermissions,
      setPermissions: state.setPermissions,
    }))
  );

  const setError = useGlobalStore((state) => state.setError);

  const [localPermissions, setLocalPermissions] = useState<PermissionSection[]>(
    []
  );

  const fetchRoles = async () => {
    if (!selectedRole && roleId) {
      try {
        await getRoles();
        setRoleById(roleId);
      } catch (error) {
        setError(error instanceof Error ? error.message : "Something went wrong");
        return;
      }
    }

    try {
      await getPermissions(roleId);
    } catch (error) {
      setError(error instanceof Error ? error.message : "Something went wrong");
    }
  };

  useEffect(() => {
    if (permissions) {
      setLocalPermissions(permissions);
    }
  }, [permissions]);

  useEffect(() => {
    fetchRoles();
  }, []);

  const togglePermission = (sectionIdx: number, permIdx: number) => {
    setLocalPermissions((prev) => {
      return prev.map((section, sIdx) => {
        if (sIdx !== sectionIdx) return section;
        return {
          ...section,
          permissions: section.permissions.map((perm, pIdx) => {
            if (pIdx !== permIdx) return perm;
            return { ...perm, checked: !perm.checked };
          }),
        };
      });
    });
  };

  return (
    <Layout>
      <div className="font-bold text-2xl mb-6">Edit Permissions</div>

      {!localPermissions || localPermissions.length === 0 ? (
        <div className="flex justify-center items-center">
          <Spinner size={30} />
        </div>
      ) : (
        <div>
          {/* Header */}
          <div className="flex items-center space-x-3 mb-6">
            <FaArrowLeft
              className="text-[#EA33C0] cursor-pointer"
              onClick={() => {
                setPermissions(null);
                navigate(-1);
              }}
            />
            <h2 className="text-xl font-medium text-gray-800">
              {(selectedRole && selectedRole.name) || "N.A"}
            </h2>
          </div>

          <div className="border-t-2 border-[#EA33C0] mb-6" />

          {/* Permission Sections */}
          {localPermissions.map((section, idx) => (
            <div key={idx} className="mb-10">
              <h3 className="text-xs text-gray-400 font-semibold uppercase">
                {section.title}
              </h3>
              <hr className="border-1 border-slate-300 mb-4 mt-2" />
              <div className="space-y-4">
                {section.permissions.map((perm, i) => (
                  <div key={perm.id} className={`flex items-center space-x-3`}>
                    <Checkbox
                      checked={perm.checked}
                      onChange={() => togglePermission(idx, i)}
                    />
                    <span className="text-sm font-medium text-gray-900">
                      {perm.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="border-t border-[#EA33C0] mb-6" />
          <Button
            type="secondary"
            onClick={async () => {
              try {
                setIsLoading(true);
                await savePermissions(localPermissions);
              } catch (error) {
                setError(error instanceof Error ? error.message : "Something went wrong");
              } finally {
                setIsLoading(false);
              }
            }}
            isLoading={isLoading}
          >
            Save Changes
          </Button>
        </div>
      )}
    </Layout>
  );
};

export default UserPermissions;
